import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  DateInput,
  FormCheckBox,
  FormContainer,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useState } from "react";
import MainApi from "../../../../../../services/mainApi.service";
import {
  createTicketAutoTransferRule,
  getTicketAutoTransferRuleById,
  updateTicketAutoTransferRuleById,
} from "../../../../../../services/ticketAutoTransferRule.service";
import Message from "./Message";
import {
  TTicketAutoTransferRuleForm,
  TicketAutoTransferRuleSchema,
} from "./TicketAutoTransferRuleSchema";
import TicketWorkspace from "./TicketWorkspace";
import { TicketChannelStep } from "./TicketChannelStep/TicketChannelStep";
import { TicketChannelStepTransfer } from "./TicketChannelStep/TicketChannelStepTransfer";
import { Minutes } from "./Minutes";
import { TransferType } from "./TransferType";
import TicketServiceFields from "./TicketServiceFields";

const TICKET_TYPE_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const ticketTypeService = new MainApi(TICKET_TYPE_ROUTE);

const translationPath = "page.settings.crm.ticketAutoTransferRules.";

const TicketAutoTransferRule = () => {
  const { t } = useTranslation();

  const [ticketWorkspaceId, setTicketWorkspaceId] = useState<string>("");

  const [message, setMessage] = useState("");

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutoTransferRule
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutoTransferRuleById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutoTransferRuleById
  );

  const handleSave = (v: TTicketAutoTransferRuleForm) => {
    return save({
      ...v,
      ticketChannelId: v.ticketChannel ? v.ticketChannel.id : "",
      ticketChannelStepId: v.ticketChannelStep?.id!,
      ticketChannelStepTransferId: v.ticketChannelStepTransfer?.id!,
      ticketTypeId: v.ticketType.id,
      ticketServiceId: v.ticketService?.id,
      ticketServiceAreasId: v.ticketServiceAreas?.id,
      ticketAutoTransferRuleHourAtendId:
        v.ticketAutoTransferRuleHourAtend?.id!,
      minutes: v.minutes ? Number(v.minutes) : undefined,
      initialDate: v.initialDate.toISOString(),
      endDate: v.endDate.toISOString(),
      transferIndifferentPerson: v.transferIndifferentPerson,
    });
  };

  const updateHandler = (v: TTicketAutoTransferRuleForm, id: string) => {
    return update({
      item: {
        ...v,
        ticketChannelId: v.ticketChannel ? v.ticketChannel.id : "",
        ticketChannelStepId: v.ticketChannelStep?.id!,
        ticketChannelStepTransferId: v.ticketChannelStepTransfer?.id!,
        ticketTypeId: v.ticketType.id,
        ticketServiceId: v.ticketService?.id,
        ticketServiceAreasId: v.ticketServiceAreas?.id,
        ticketAutoTransferRuleHourAtendId:
          v.ticketAutoTransferRuleHourAtend?.id!,
        minutes: v.minutes ? Number(v.minutes) : undefined,
        initialDate: v.initialDate.toISOString(),
        endDate: v.endDate.toISOString(),
        transferIndifferentPerson: v.transferIndifferentPerson,
      },
      id: id,
    });
  };

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TTicketAutoTransferRuleForm>> => {
      const { data, success } = await get(id);

      if (data) {
        if (data.ticketChannel && data.ticketChannel.ticketWorkspaceId) {
          setTicketWorkspaceId(data.ticketChannel.ticketWorkspaceId);
        }

        if (data.message && data.message.length !== 0) {
          setMessage(data.message);
        }

        const newData: TTicketAutoTransferRuleForm = {
          ...data,
          ticketChannelTransfer: data.ticketChannelStepTransfer.ticketChannel,
          ticketChannelStep: data.ticketChannelStep,
          ticketService: data.ticketService,
          ticketServiceAreas: data.ticketServiceAreas ?? null,
          ticketAutoTransferRuleHourAtend:
            data.ticketAutoTransferRuleHourAtend ?? null,
          minutes: data.minutes?.toString(),
          initialDate: new Date(data.initialDate),
          endDate: new Date(data.endDate),
          transferIndifferentPerson: data.transferIndifferentPerson,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TTicketAutoTransferRuleForm>
      saveFunction={handleSave}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "ticket_auto_transfer_rule")}
      schema={TicketAutoTransferRuleSchema}
      subtitle={t(translationPath + "new_ticket_auto_transfer_rule")}
      subtitleWatchField={"ticketType.name"}
      defaultValues={{
        transfer: true,
        endOfDay: false,
        message:
          message.length !== 0
            ? message
            : "Seu atendimento foi transferido por estar muito tempo sem sua interação, se precisar retomar esta conversa informe aos nossos atendentes o número de protocolo {{protocolNumber}}.",
      }}
      fields={() => (
        <Grid container spacing={2}>
          <TransferType />

          <Minutes />

          <Grid item xs={12} sm={6}>
            <AutoComplete
              params={{ identifier: "TICKET_TYPE" }}
              getOptionLabel={(option) => {
                return option.name;
              }}
              size="small"
              label={t(translationPath + "ticket_type")}
              name="ticketType"
              request={ticketTypeService.list}
            />
          </Grid>

          <TicketWorkspace ticketWorkspaceId={ticketWorkspaceId} />

          <TicketChannelStep />

          <Grid item xs={12} sm={3}>
            <DateInput
              name="initialDate"
              label={t(translationPath + "initial_date")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <DateInput name="endDate" label={t(translationPath + "end_date")} />
          </Grid>

          <TicketServiceFields />

          <Grid item xs={12} sm={12}>
            <FormCheckBox
              name="transferIndifferentPerson"
              label={`${t(translationPath + "transfer_indifferent_person")}`}
            />
          </Grid>

          <Grid item xs={12} sx={{ paddingTop: "0 !important" }}>
            <TicketChannelStepTransfer />
          </Grid>

          <Message />
        </Grid>
      )}
    />
  );
};

export default TicketAutoTransferRule;
